import type { BookingWindowKind, ChargeBasis, TaxChargeKind } from '@/types/generated/rate-data';

import { FILTER_KEYS } from './filters';

/**
 * Rate Constants
 *
 * These constants define the rate configuration values and are used
 * for type-safe access throughout the application.
 */

/** Radix Select reserves empty string for "no selection"; use this for "Create as new" options. */
export const SELECT_VALUE_CREATE_NEW = '__new__';

/** Matches `App\Services\ExtractionValidator` severity values. */
export const EXTRACTION_VALIDATION_SEVERITY = {
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
} as const;

export type ExtractionValidationSeverity = (typeof EXTRACTION_VALIDATION_SEVERITY)[keyof typeof EXTRACTION_VALIDATION_SEVERITY];

/**
 * Internal view states for the `RateShareDialog`. Not sent to the backend —
 * purely drives which step of the dialog is rendered.
 */
export const RATE_SHARE_DIALOG_VIEWS = {
    CHOOSE: 'choose',
    CONFIRM_ALL: 'confirm-all',
    SPECIFIC: 'specific',
    CONFIRM_PRIVATE: 'confirm-private',
} as const;

export type RateShareDialogView = (typeof RATE_SHARE_DIALOG_VIEWS)[keyof typeof RATE_SHARE_DIALOG_VIEWS];

export const RATE_SHEET_CONSTANTS = {
    TYPES: {
        RATE_SHEET: 'rate_sheet',
        CONTRACT: 'contract',
        BROCHURE: 'brochure',
        OTHER: 'other',
    },

    STATUSES: {
        PENDING: 'pending',
        EXTRACTING: 'extracting',
        REVIEW_PENDING: 'review_pending',
        REVIEWED: 'reviewed',
        PROCESSING: 'processing',
        COMPLETED: 'completed',
        FAILED: 'failed',
        CANCELLED: 'cancelled',
    },

    RATE_TYPES: {
        SPECIAL: 'special',
        RACK: 'rack',
        NETT: 'nett',
        STO: 'sto',
        STANDARD: 'standard',
        PROMOTIONAL: 'promotional',
    },

    // Early Booker (advance-purchase) lead-time units — maps to Tourplan
    // SellBeforeType (days → D, months → M).
    EARLY_BOOKER_UNITS: {
        DAYS: 'days',
        MONTHS: 'months',
    },

    // Which days a rate applies to — drives Tourplan Apply_* flags. midweek =
    // Sun–Thu, weekend = Fri–Sat, weekend_fri_sun = Fri–Sun. Mirrors the
    // constants on App\Support\DayBand.
    DAY_BANDS: {
        ALL: 'all',
        MIDWEEK: 'midweek',
        WEEKEND: 'weekend',
        WEEKEND_FRI_SUN: 'weekend_fri_sun',
    },
} as const;

export type RateSheetStatus = (typeof RATE_SHEET_CONSTANTS.STATUSES)[keyof typeof RATE_SHEET_CONSTANTS.STATUSES];
export type RateSheetType = (typeof RATE_SHEET_CONSTANTS.TYPES)[keyof typeof RATE_SHEET_CONSTANTS.TYPES];

/**
 * Why a rate was archived. Mirrors `App\Enums\RateArchiveReason`.
 */
export const RATE_ARCHIVE_REASONS = {
    SEASON_ENDED: 'season_ended',
    SHEET_EXPIRED: 'sheet_expired',
    MANUAL: 'manual',
    SUPERSEDED: 'superseded',
} as const;

export type RateArchiveReasonValue = (typeof RATE_ARCHIVE_REASONS)[keyof typeof RATE_ARCHIVE_REASONS];

/**
 * The reviewer's answer on Approve for a property that looks like one in the
 * catalogue. Mirrors `App\Enums\PropertyLinkDecision`.
 */
export const PROPERTY_LINK_DECISIONS = {
    MERGE: 'merge',
    NEW: 'new',
} as const;

export type PropertyLinkDecisionValue = (typeof PROPERTY_LINK_DECISIONS)[keyof typeof PROPERTY_LINK_DECISIONS];

/**
 * What an extracted property shares with the catalogue property it looks
 * like. Mirrors `App\Enums\PropertyMatchReason`.
 */
export const PROPERTY_MATCH_REASONS = {
    NAME: 'name',
    SUPPLIER: 'supplier',
    LOCATION: 'location',
} as const;

export type PropertyMatchReasonValue = (typeof PROPERTY_MATCH_REASONS)[keyof typeof PROPERTY_MATCH_REASONS];

/**
 * The two views of the graph editor's Properties & Services tree: live rates, or only what has been archived.
 */
export const GRAPH_EDITOR_RATE_VIEWS = {
    CURRENT: 'current',
    ARCHIVED: 'archived',
} as const;

export type GraphEditorRateViewValue = (typeof GRAPH_EDITOR_RATE_VIEWS)[keyof typeof GRAPH_EDITOR_RATE_VIEWS];

/** Tabs of the Properties & Services list. */
export const CATALOGUE_LIST_VIEWS = { PROPERTIES: 'properties', SERVICES: 'services', ARCHIVED: 'archived' } as const;

export type CatalogueListViewValue = (typeof CATALOGUE_LIST_VIEWS)[keyof typeof CATALOGUE_LIST_VIEWS];

/** One-click filters above the property and service provider lists. */
/** The page-header slot the catalogue sidebar renders its search and filters into (graph editor). */
export const CATALOGUE_FILTERS_SLOT_ID = 'catalogue-filters';

export const CATALOGUE_QUICK_FILTERS = {
    ALL: 'all',
    NEEDS_ATTENTION: 'needs_attention',
    NO_CURRENT_RATES: 'no_current_rates',
    UNPUBLISHED: 'unpublished',
} as const;

export type CatalogueQuickFilterValue = (typeof CATALOGUE_QUICK_FILTERS)[keyof typeof CATALOGUE_QUICK_FILTERS];

/** The one status a catalogue row (property or provider) shows, most urgent first. */
export const CATALOGUE_ROW_STATUSES = {
    DELETING: 'deleting',
    NEW: 'new',
    ATTENTION: 'attention',
    NO_RATES: 'no_rates',
    EDITED: 'edited',
    COMPLETE: 'complete',
} as const;

export type CatalogueRowStatusValue = (typeof CATALOGUE_ROW_STATUSES)[keyof typeof CATALOGUE_ROW_STATUSES];

/** The supplier filter's "All suppliers" choice; every other value is a managed supplier id (a uuid). */
export const CATALOGUE_SUPPLIER_FILTER_ALL = 'all';

/**
 * How a rate-sheet document entered the platform. Mirrors `App\Enums\DocumentSource`.
 * Agent `OWN_UPLOAD` sheets are permanently private and never shareable.
 */
export const DOCUMENT_SOURCE = {
    SUPPLIER_UPLOAD: 'supplier_upload',
    OWN_UPLOAD: 'own_upload',
} as const;

export type DocumentSourceValue = (typeof DOCUMENT_SOURCE)[keyof typeof DOCUMENT_SOURCE];

export const getRateSheetStatuses = (t: (key: string) => string) => [
    { value: RATE_SHEET_CONSTANTS.STATUSES.PENDING, label: t('rates.status.pending') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING, label: t('rates.status.extracting') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.REVIEW_PENDING, label: t('rates.status.review_pending') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.REVIEWED, label: t('rates.status.reviewed') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.PROCESSING, label: t('rates.status.processing') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.COMPLETED, label: t('rates.status.completed') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.FAILED, label: t('rates.status.failed') },
    { value: RATE_SHEET_CONSTANTS.STATUSES.CANCELLED, label: t('rates.status.cancelled') },
];

/**
 * What a rates-index search match names — something the sheet describes,
 * shown back on the row so a result says why it is there.
 *
 * Mirrors `App\Enums\RateSheetSearchMatchKind`.
 */
export const RATE_SHEET_SEARCH_MATCH_KINDS = {
    PROPERTY: 'property',
    SERVICE_PROVIDER: 'service_provider',
    LOCATION: 'location',
    REGION: 'region',
    COUNTRY: 'country',
    ROOM: 'room',
    MEAL_PLAN: 'meal_plan',
    RATE: 'rate',
    SEASON: 'season',
    SERVICE_CATEGORY: 'service_category',
    SERVICE: 'service',
    ADD_ON: 'add_on',
    SPECIAL: 'special',
} as const;

export type RateSheetSearchMatchKindValue = (typeof RATE_SHEET_SEARCH_MATCH_KINDS)[keyof typeof RATE_SHEET_SEARCH_MATCH_KINDS];

/** Which list a rates-index search suggestion came from — picking one applies that filter. */
export const RATE_SHEET_SUGGESTION_GROUPS = {
    PLACE: 'place',
    SUPPLIER: 'supplier',
} as const;

/**
 * Which guests a rate may be sold to.
 *
 * Mirrors `App\Enums\RateMarket`.
 */
export const RATE_MARKETS = {
    INTERNATIONAL: 'international',
    SA_RESIDENT: 'sa_resident',
    SADC_RESIDENT: 'sadc_resident',
} as const;

export type RateMarketValue = (typeof RATE_MARKETS)[keyof typeof RATE_MARKETS];

/**
 * A value the sheet does not state, as a select offers it: a "Priced for"
 * the sheet leaves open (the grid's Not stated column) and a tax's basis
 * left unstated. Never written to a rate: choosing it clears the field.
 */
export const NOT_STATED = 'not_stated' as const;

/**
 * The bounds of a booking window, as the schema holds them
 * (`App\Domain\Schema\Entities\BookingWindowSchema`): a lead time or a
 * last-minute window of at most 730 days, or 24 months.
 */
export const BOOKING_WINDOW_LIMITS = {
    MAX_DAYS: 730,
    MAX_MONTHS: 24,
} as const;

/**
 * The most a child band may be charged as a percentage of the per person
 * sharing price (`ChildBandSchema::$percent_of_sharing`, max:100).
 */
export const MAX_PERCENT_OF_SHARING = 100;

/**
 * The longest name a tax or levy row keeps, the draft rules' `max:255` and
 * the bound `App\Support\TaxLevyNormalizer::NAME_MAX_LENGTH` cuts a legacy
 * string's name to.
 */
export const TAX_LEVY_NAME_MAX_LENGTH = 255;

/**
 * How a tax or levy is charged: a percentage of the price or a fixed amount.
 *
 * Mirrors `App\Enums\Rates\TaxChargeKind`.
 */
export const TAX_CHARGE_KINDS = {
    PERCENT: 'percent',
    AMOUNT: 'amount',
} as const satisfies Record<string, TaxChargeKind>;

/**
 * When a rate may be booked: only within N days of arrival (last minute), or
 * at least N days or months before it (early booker).
 *
 * Mirrors `App\Enums\Rates\BookingWindowKind`.
 */
export const BOOKING_WINDOW_KINDS = {
    WITHIN: 'within',
    AT_LEAST: 'at_least',
} as const satisfies Record<string, BookingWindowKind>;

/**
 * Who a fixed-amount tax or levy is counted on.
 *
 * Mirrors `App\Enums\Rates\ChargeBasis`.
 */
export const TAX_CHARGE_BASES = {
    PER_PERSON: 'per_person',
    PER_ROOM: 'per_room',
    PER_GROUP: 'per_group',
} as const satisfies Record<string, ChargeBasis>;

/**
 * How a child age band is charged in the review's Children settings: a price
 * per band (typed in the grid), or a percentage of the per person sharing
 * price (`percent_of_sharing`, P1-11), or free (a price of 0). UI only: a
 * band stores either its `price` or its `percent_of_sharing`.
 */
export const CHILD_CHARGE_KINDS = {
    FREE: 'free',
    AMOUNT: 'amount',
    PERCENT_OF_SHARING: 'percent_of_sharing',
} as const;

export type ChildChargeKind = (typeof CHILD_CHARGE_KINDS)[keyof typeof CHILD_CHARGE_KINDS];

/**
 * Markets that restrict who may buy a rate to residents — an itinerary priced
 * for one says so next to its rates. Matches `RateMarket::SaResident` /
 * `RateMarket::SadcResident`.
 */
export const RESIDENT_RATE_MARKETS: readonly RateMarketValue[] = [RATE_MARKETS.SA_RESIDENT, RATE_MARKETS.SADC_RESIDENT];

export const isResidentMarket = (market: string | null | undefined): market is RateMarketValue =>
    !!market && (RESIDENT_RATE_MARKETS as readonly string[]).includes(market);

/**
 * The "all guests" choice in a market select. Radix Select cannot hold an
 * empty value, so this stands in for `null` — no market, sold to everyone.
 */
export const RATE_MARKET_ANY = '__any__';

export const getRateMarkets = (t: (key: string) => string) => [
    { value: RATE_MARKETS.INTERNATIONAL, label: t('rates.service_rate_market.international') },
    { value: RATE_MARKETS.SA_RESIDENT, label: t('rates.service_rate_market.sa_resident') },
    { value: RATE_MARKETS.SADC_RESIDENT, label: t('rates.service_rate_market.sadc_resident') },
];

export const getRateTypes = (t: (key: string) => string) => [
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.SPECIAL, label: t('rates.rate_type.special') },
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.RACK, label: t('rates.rate_type.rack') },
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.NETT, label: t('rates.rate_type.nett') },
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.STO, label: t('rates.rate_type.sto') },
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.STANDARD, label: t('rates.rate_type.standard') },
    { value: RATE_SHEET_CONSTANTS.RATE_TYPES.PROMOTIONAL, label: t('rates.rate_type.promotional') },
];

export const ACCEPTED_UPLOAD_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'text/csv',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
] as const;

export const ACCEPTED_UPLOAD_EXTENSIONS = '.pdf,.xlsx,.xls,.docx,.doc,.csv,.ods,.odt';

/**
 * Whether a rate document is a type the server will take. Checks the extension, not
 * the MIME type: browsers report CSV and OpenDocument types inconsistently, and the
 * server's `mimes:` rule names these same extensions. The input's `accept` only
 * filters the picker, so a dropped or programmatically set file must be checked too.
 */
export const isAcceptedUploadFile = (file: File): boolean => {
    const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : '';

    return extension !== '' && ACCEPTED_UPLOAD_EXTENSIONS.split(',').includes(extension);
};

export const MAX_UPLOAD_SIZE_MB = 50;

export const PLACEHOLDER_PROPERTY_NAME = 'Unknown Property';
export const PLACEHOLDER_LOCATION = 'Not specified';
export const PLACEHOLDER_ROOM_TYPE = 'Unknown';

/** The currency a new price takes when nothing (provider, sheet) names one: a service price row, an activity. */
export const DEFAULT_RATE_CURRENCY = 'ZAR';

export const VALID_CURRENCIES = new Set(['ZAR', 'USD', 'EUR', 'GBP', 'BWP', 'MZN', 'NAD', 'TZS', 'KES']);

export const SELL_MODES = {
    PER_ROOM: 'per_room',
    PER_PERSON: 'per_person',
} as const;

export type SellMode = (typeof SELL_MODES)[keyof typeof SELL_MODES];

export const RATE_MODES = {
    MANUAL: 'manual',
    AUTO: 'auto',
    DERIVED: 'derived',
} as const;

export type RateMode = (typeof RATE_MODES)[keyof typeof RATE_MODES];

/**
 * Document kinds in the rate review/upload flow. A `rate_sheet` is the primary
 * uploaded rate document; a `fact_sheet` is an attached supplementary document.
 * Used as `document_type` form values, extraction source identifiers, and PDF
 * preview-tab identifiers.
 */
export const RATE_DOCUMENT_KINDS = {
    RATE_SHEET: 'rate_sheet',
    FACT_SHEET: 'fact_sheet',
} as const;

export type RateDocumentKind = (typeof RATE_DOCUMENT_KINDS)[keyof typeof RATE_DOCUMENT_KINDS];

/**
 * Lifecycle states for an agent-facing rate share row, surfaced by
 * `AgentRateController::index()`. Action affordances and badge styling vary
 * by state. Mirrors `App\Enums\AgentRateState`.
 */
export const AGENT_RATE_STATES = {
    ACTIVE: 'active',
    REQUIRES_SIGNATURE: 'requires_signature',
    AWAITING_ACCEPTANCE: 'awaiting_acceptance',
    PRE_SIGNUP: 'pre_signup',
} as const;

export type AgentRateState = (typeof AGENT_RATE_STATES)[keyof typeof AGENT_RATE_STATES];

/**
 * How an agent holds a supplier's rates. Mirrors the PHP `RateShareKind` enum.
 * `supplier_managed` is a deliberately separate system from normal shared rates.
 */
export const RATE_SHARE_KINDS = {
    SHARED: 'shared',
    SUPPLIER_MANAGED: 'supplier_managed',
} as const;

export type RateShareKind = (typeof RATE_SHARE_KINDS)[keyof typeof RATE_SHARE_KINDS];

/** Sidebar / editor selection in unified rate review (properties + service provider tree) */
export const RATE_REVIEW_SELECTED_ITEM_TYPES = {
    PROPERTY: 'property',
    RATE: 'rate',
    PROVIDER: 'provider',
    CATEGORY: 'category',
    SERVICE: 'service',
} as const;

export type RateReviewSelectedItemType = (typeof RATE_REVIEW_SELECTED_ITEM_TYPES)[keyof typeof RATE_REVIEW_SELECTED_ITEM_TYPES];

/**
 * Carried by each row a person clicks to open something on the review screen
 * — a checklist row, a rate in Rooms & rates, a service on its category — as
 * `<kind>:<id>` (a {@link RATE_REVIEW_SELECTED_ITEM_TYPES} value), so the
 * Guide's pickers can point at the row they picked. See `reviewEntityAttrs()`.
 */
export const REVIEW_ENTITY_ATTRIBUTE = 'data-review-entity';

/**
 * The three-way "rates" filter on the review tree sidebar
 * ({@see useReviewTreeFilter}): show everything, only items that have at
 * least one rate, or only items with none.
 */
export const REVIEW_TREE_RATES_FILTER = {
    ANY: 'any',
    WITH: 'with',
    WITHOUT: 'without',
} as const;

export type ReviewTreeRatesFilter = (typeof REVIEW_TREE_RATES_FILTER)[keyof typeof REVIEW_TREE_RATES_FILTER];

/**
 * Kinds of finalize/publish blocker surfaced by the blocking-issues banner and
 * dialog. Unlike the missing-field stepper's soft warnings, each of these
 * mirrors a server-side `required` rule and cannot be dismissed.
 */
export const BLOCKING_ISSUE_KINDS = {
    PROPERTY_RATES: 'property_rates',
    RATE_FIELD: 'rate_field',
    /** A property field Approve requires (its name). */
    PROPERTY_FIELD: 'property_field',
    /** Two live rates nothing tells apart (spec D3): Approve refuses them. */
    INDISTINGUISHABLE_RATES: 'indistinguishable_rates',
} as const;

export type BlockingIssueKind = (typeof BLOCKING_ISSUE_KINDS)[keyof typeof BLOCKING_ISSUE_KINDS];

/**
 * The rate fields a reviewer must fill in before a sheet can be approved —
 * each mirrors a `required` rule in `FinalizeExtractionRequest`. The review
 * editor marks them with a `*`, and the completeness list files them under
 * "Required" even when their value only warns (a price of 0).
 */
export const REQUIRED_RATE_FIELDS = {
    ROOM_TYPE: 'room_type',
    PRICE: 'price',
    CURRENCY: 'currency',
    SEASONS: 'seasons',
} as const;

/**
 * Sub-section tabs inside the rate (room) editor. Shared by the editor itself,
 * the review tree sidebar's deep-links and the missing-field stepper — all three
 * must agree on the ids or a deep-link lands on the wrong tab.
 */
export const RATE_EDITOR_TABS = {
    DETAILS: 'details',
    INCLUSIONS: 'inclusions',
    TAXES: 'taxes',
    CHILDREN: 'children',
    AVAILABILITY: 'availability',
    ACTIVITIES: 'activities',
    IMAGES: 'images',
} as const;

export type RateEditorTab = (typeof RATE_EDITOR_TABS)[keyof typeof RATE_EDITOR_TABS];

/**
 * The tabs of a price list's "Rate settings" in the rate review's Rooms &
 * rates grid (`RateBlockSettings.vue`). They edit every rate in the list at
 * once, so they are not the one-rate editor's sections; {@see RATE_EDITOR_TAB_TO_BLOCK_TAB}
 * says where a section asked for by a "Fix" or the Guide lives here.
 */
export const RATE_BLOCK_SETTINGS_TABS = {
    CHILDREN: 'children',
    INCLUSIONS: 'inclusions',
    STAY: 'stay',
    DETAILS: 'details',
} as const;

export type RateBlockSettingsTab = (typeof RATE_BLOCK_SETTINGS_TABS)[keyof typeof RATE_BLOCK_SETTINGS_TABS];

/**
 * The rate fields each Rate settings tab edits. A review scope hides a field
 * the scope never reads; a tab whose fields are ALL hidden is not drawn.
 */
export const RATE_BLOCK_TAB_FIELDS: Record<RateBlockSettingsTab, readonly string[]> = {
    [RATE_BLOCK_SETTINGS_TABS.CHILDREN]: ['child_rates', 'child_breakfast_price'],
    [RATE_BLOCK_SETTINGS_TABS.INCLUSIONS]: ['includes', 'excludes', 'taxes_and_levies', 'compulsory_extras', 'optional_extras', 'rate_activities'],
    [RATE_BLOCK_SETTINGS_TABS.STAY]: [
        'min_stay',
        'max_stay',
        'day_band',
        'booking_window',
        'early_booker_days',
        'early_booker_unit',
        'minimum_stay_periods',
        'closed_periods',
    ],
    [RATE_BLOCK_SETTINGS_TABS.DETAILS]: [
        'room_type',
        'meal_plan',
        'market',
        'rate_type',
        'rate_set',
        'price_unit',
        'currency',
        'package_nights',
        'pricing_basis',
        'rooms',
        'occupancy',
        'max_occupancy',
        'single_supplement',
        'single_supplement_unit',
        'stay_pay_stay',
        'stay_pay_pay',
        'stay_pay_valid_from',
        'stay_pay_valid_to',
        'notes',
    ],
};

/** The Rate settings tab holding what a one-rate editor section held. */
export const RATE_EDITOR_TAB_TO_BLOCK_TAB: Record<RateEditorTab, RateBlockSettingsTab> = {
    [RATE_EDITOR_TABS.DETAILS]: RATE_BLOCK_SETTINGS_TABS.DETAILS,
    [RATE_EDITOR_TABS.INCLUSIONS]: RATE_BLOCK_SETTINGS_TABS.INCLUSIONS,
    [RATE_EDITOR_TABS.TAXES]: RATE_BLOCK_SETTINGS_TABS.INCLUSIONS,
    [RATE_EDITOR_TABS.ACTIVITIES]: RATE_BLOCK_SETTINGS_TABS.INCLUSIONS,
    [RATE_EDITOR_TABS.CHILDREN]: RATE_BLOCK_SETTINGS_TABS.CHILDREN,
    [RATE_EDITOR_TABS.AVAILABILITY]: RATE_BLOCK_SETTINGS_TABS.STAY,
    [RATE_EDITOR_TABS.IMAGES]: RATE_BLOCK_SETTINGS_TABS.DETAILS,
};

/** Sub-section tabs inside the property editor. Same three-way contract as {@see RATE_EDITOR_TABS}. */
export const PROPERTY_EDITOR_TABS = {
    OVERVIEW: 'overview',
    RATES: 'rates',
    DETAILS: 'details',
    LOCATION: 'location',
    POLICIES: 'policies',
    ACTIVITIES: 'activities',
    IMAGES: 'images',
    /** The rate sheet(s) the property came from, shown inline (graph editor only). */
    SOURCE: 'source',
} as const;

export type PropertyEditorTab = (typeof PROPERTY_EDITOR_TABS)[keyof typeof PROPERTY_EDITOR_TABS];

/** Sub-section tabs inside the service provider editor. Same contract as {@see PROPERTY_EDITOR_TABS}. */
export const PROVIDER_EDITOR_TABS = {
    OVERVIEW: 'overview',
    SERVICES: 'services',
    DETAILS: 'details',
    LOCATION: 'location',
    POLICIES: 'policies',
    IMAGES: 'images',
    /** The rate sheet(s) the provider came from, shown inline (graph editor only). */
    SOURCE: 'source',
} as const;

export type ProviderEditorTab = (typeof PROVIDER_EDITOR_TABS)[keyof typeof PROVIDER_EDITOR_TABS];

/** Where a provider's validity window stands today; null (no chip) when it is current or open-ended. */
export const PROVIDER_VALIDITY_STATUSES = {
    EXPIRED: 'expired',
    EXPIRING: 'expiring',
} as const;

export type ProviderValidityStatusValue = (typeof PROVIDER_VALIDITY_STATUSES)[keyof typeof PROVIDER_VALIDITY_STATUSES];

/** A provider whose `valid_to` falls within this many days is flagged as expiring. */
export const PROVIDER_EXPIRING_WITHIN_DAYS = 30;

/** Services per page on the provider Overview's summary list (paged client-side). */
export const PROVIDER_OVERVIEW_SERVICES_PER_PAGE = 10;

/** Service rows a category on the provider's Services tab shows before "Show N more". */
export const PROVIDER_CATEGORY_VISIBLE_SERVICES = 10;

/**
 * Filter keys for the rate-sheet documents index ({@see DocumentFilters}).
 * The flat keys mirror the backend query params; the `*_RANGE` keys are
 * UI-only composites that get mirrored down to their flat counterparts
 * before the request is sent.
 */
export const DOCUMENT_FILTER_KEYS = {
    SEARCH: FILTER_KEYS.SEARCH,
    BRAND_IDS: 'brand_ids',
    DOCUMENT_TYPE: 'document_type',
    RATE_TYPE: 'rate_type',
    MARKET: FILTER_KEYS.MARKET,
    STATUS: FILTER_KEYS.STATUS,
    SEARCHABLE: 'searchable',
    SOURCE: FILTER_KEYS.SOURCE,
    DISCOUNT_MIN: 'discount_min',
    DISCOUNT_MAX: 'discount_max',
    START_FROM: 'start_from',
    START_TO: 'start_to',
    EXPIRES_FROM: 'expires_from',
    EXPIRES_TO: 'expires_to',
    DISCOUNT_RANGE: 'discount_range',
    START_RANGE: 'start_range',
    EXPIRES_RANGE: 'expires_range',

    /** `key:direction` sort pair, shared with every other list page. */
    SORT: FILTER_KEYS.SORT,

    /**
     * Rates index: graph property / service-provider ids, from the place chip
     * or a search suggestion. The rates index also reads `SUPPLIER_IDS` below,
     * as managed supplier ids.
     */
    PLACE_IDS: 'place_ids',

    /** Rate requests: who the request went to, and when it was sent. */
    SUPPLIER_IDS: 'supplier_ids',
    SENT_FROM: 'sent_from',
    SENT_TO: 'sent_to',
    SENT_RANGE: 'sent_range',

    /** Agent rates: brand scope and extraction progress. */
    ALL_BRANDS: 'all_brands',
    EXTRACTION_STATUS: 'extraction_status',
    STATE: 'state',

    /** Rates & STOs: agreement kind and the two per-artefact statuses. */
    KIND: 'kind',
    SHARED: 'shared',
    STATUS_RATE_SHEET: 'status_rate_sheet',
    STATUS_AGREEMENT: 'status_agreement',
} as const;

export type DocumentFilterKey = (typeof DOCUMENT_FILTER_KEYS)[keyof typeof DOCUMENT_FILTER_KEYS];

/**
 * The UI-only composite range keys. The backend reads only their flat
 * counterparts, so pass these as `useFilters`' `uiOnlyKeys` to keep them out
 * of the request and the URL: otherwise every range is written twice.
 */
export const DOCUMENT_RANGE_FILTER_KEYS: readonly string[] = [
    DOCUMENT_FILTER_KEYS.DISCOUNT_RANGE,
    DOCUMENT_FILTER_KEYS.START_RANGE,
    DOCUMENT_FILTER_KEYS.EXPIRES_RANGE,
    DOCUMENT_FILTER_KEYS.SENT_RANGE,
];

/**
 * The screen a pending graph change was made on, shown in the pending-changes
 * review so a price typed on the Rate Manager is recognisable on Properties &
 * Services (and the other way round). Mirrors `App\Enums\GraphChangeSource`.
 */
export const GRAPH_CHANGE_SOURCES = {
    GRAPH_EDITOR: 'graph_editor',
    RATE_MANAGER: 'rate_manager',
    POLICY_CONVERSION: 'policy_conversion',
} as const;

export type GraphChangeSourceValue = (typeof GRAPH_CHANGE_SOURCES)[keyof typeof GRAPH_CHANGE_SOURCES];

/**
 * What one graph change does to its node. Create / update / delete are what a
 * pending draft carries; archive / restore only ever appear on published
 * history. Mirrors `App\Enums\GraphDraftAction`.
 */
export const GRAPH_DRAFT_ACTIONS = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    ARCHIVE: 'archive',
    RESTORE: 'restore',
} as const;

export type GraphDraftActionValue = (typeof GRAPH_DRAFT_ACTIONS)[keyof typeof GRAPH_DRAFT_ACTIONS];

/**
 * Why a pending change deserves a second look before publishing: a live value
 * emptied, or a count dropping to 0. Mirrors `App\Enums\GraphPendingChangeRisk`.
 */
export const GRAPH_PENDING_CHANGE_RISKS = {
    CLEARED: 'cleared',
    ZEROED: 'zeroed',
} as const;

export type GraphPendingChangeRiskValue = (typeof GRAPH_PENDING_CHANGE_RISKS)[keyof typeof GRAPH_PENDING_CHANGE_RISKS];

/**
 * Node kinds the graph editor versions. Only `PROPERTY` and `SERVICE_PROVIDER`
 * can scope the history (they are the roots everything else hangs under).
 * Mirrors `App\Enums\GraphEntityType`.
 */
export const GRAPH_ENTITY_TYPES = {
    PROPERTY: 'property',
    RATE: 'rate',
    SERVICE_PROVIDER: 'service_provider',
    SERVICE_CATEGORY: 'service_category',
    SERVICE: 'service',
} as const;

export type GraphEntityTypeValue = (typeof GRAPH_ENTITY_TYPES)[keyof typeof GRAPH_ENTITY_TYPES];

/** Which room slot a rate prices. Mirrors `App\Enums\Pricing\OccupancyKind`. */
export const OCCUPANCY_KINDS = {
    SINGLE: 'single',
    TWIN: 'twin',
    TRIPLE: 'triple',
    QUAD: 'quad',
    UNKNOWN: 'unknown',
} as const;
export type OccupancyKindValue = (typeof OCCUPANCY_KINDS)[keyof typeof OCCUPANCY_KINDS];

/**
 * Descriptor tokens per occupancy bucket. Mirrors the constants on
 * `App\Support\OccupancyTokens` exactly (pinned by OccupancyTokensMirrorTest).
 */
export const OCCUPANCY_TOKENS = {
    single: ['single', 'sgl', 'sole use', 'single occupancy', 'sgl use', 'sole occupancy', 'single room', 'sole', 'solo'],
    twin: [
        'twin',
        'sharing',
        'twin sharing',
        'twin share',
        'double',
        'double room',
        'dbl',
        'tw',
        'pps',
        'per person sharing',
        'per person',
        'sharing double',
    ],
    triple: ['triple', 'trpl', 'tr', 'trp'],
    quad: ['quad', 'qd', 'four'],
} as const;

/** Display order of occupancy tabs on the rate card. */
export const RATE_CARD_OCCUPANCY_ORDER: OccupancyKindValue[] = [
    OCCUPANCY_KINDS.TWIN,
    OCCUPANCY_KINDS.SINGLE,
    OCCUPANCY_KINDS.TRIPLE,
    OCCUPANCY_KINDS.QUAD,
    OCCUPANCY_KINDS.UNKNOWN,
];

/** Row qualifiers that split one room/meal plan into separate rate-card rows. */
export const RATE_CARD_QUALIFIERS = {
    RATE_TYPE: 'rate_type',
    RATE_SET: 'rate_set',
    MARKET: 'market',
    DAY_BAND: 'day_band',
    STAY: 'stay',
    EARLY_BOOKER: 'early_booker',
    BOOKING_WINDOW: 'booking_window',
    PACKAGE: 'package',
    /** Only beside a sibling list in another unit ("per unit" vs "per person"), see `rateDetailQualifiers`. */
    PRICE_UNIT: 'price_unit',
} as const;
export type RateCardQualifierValue = (typeof RATE_CARD_QUALIFIERS)[keyof typeof RATE_CARD_QUALIFIERS];

/** Key of the rate-card column for rates with no named season. */
export const RATE_CARD_ALL_YEAR_KEY = '__all_year__';

/** How a rate-card row compares to the published rates when diffing. */
export const RATE_CARD_ROW_STATUSES = {
    NEW: 'new',
    CHANGED: 'changed',
    UNCHANGED: 'unchanged',
} as const;
export type RateCardRowStatusValue = (typeof RATE_CARD_ROW_STATUSES)[keyof typeof RATE_CARD_ROW_STATUSES];

/**
 * Which price a service rate is (a `:ServiceRate`'s `rate_type`).
 *
 * Mirrors `App\Constants\ServiceProviderConstants::RATE_TYPES`.
 */
export const SERVICE_RATE_TYPES = {
    RACK: 'rack',
    STANDARD: 'standard',
    STO: 'sto',
    NETT: 'nett',
    RESIDENT: 'resident',
} as const;
export type ServiceRateTypeValue = (typeof SERVICE_RATE_TYPES)[keyof typeof SERVICE_RATE_TYPES];

/** Column order of rate types on the service price grid; any other value sorts after these. */
export const SERVICE_RATE_TYPE_ORDER: ServiceRateTypeValue[] = [
    SERVICE_RATE_TYPES.RACK,
    SERVICE_RATE_TYPES.STANDARD,
    SERVICE_RATE_TYPES.STO,
    SERVICE_RATE_TYPES.NETT,
    SERVICE_RATE_TYPES.RESIDENT,
];

/**
 * Who a service rate prices (a `:ServiceRate`'s `person_type`).
 *
 * Mirrors `App\Constants\ServiceProviderConstants::PERSON_TYPES`.
 */
export const SERVICE_PERSON_TYPES = {
    ADULT: 'adult',
    CHILD: 'child',
    INFANT: 'infant',
} as const;
export type ServicePersonTypeValue = (typeof SERVICE_PERSON_TYPES)[keyof typeof SERVICE_PERSON_TYPES];

/** Row order of person types on the service price grid; a rate with none sorts last. */
export const SERVICE_PERSON_TYPE_ORDER: ServicePersonTypeValue[] = [
    SERVICE_PERSON_TYPES.ADULT,
    SERVICE_PERSON_TYPES.CHILD,
    SERVICE_PERSON_TYPES.INFANT,
];

/**
 * How a service is priced (a `:Service`'s `pricing_model`).
 *
 * Mirrors the keys of `App\Constants\ServiceProviderConstants::PRICING_MODELS`.
 */
export const SERVICE_PRICING_MODELS = {
    PER_VEHICLE: 'per_vehicle',
    PER_PERSON: 'per_person',
    PER_PERSON_TIERED: 'per_person_tiered',
    PER_ROUTE: 'per_route',
    FLAT: 'flat',
} as const;
export type ServicePricingModelValue = (typeof SERVICE_PRICING_MODELS)[keyof typeof SERVICE_PRICING_MODELS];

/**
 * Keys of the missing-field checks (`completenessScore`) that more than one
 * screen reacts to: the attention list, the section jump bars and the editors.
 */
export const MISSING_FIELD_KEYS = {
    RATES: 'rates',
    VALIDITY: 'validity',
    /** Two seasons of one rate card row share dates (`seasonOverlapsByRate`): "to check", dismissible. */
    SEASON_OVERLAP: 'season_overlap',
    /** Prefix of a property's cancellation-policy coverage checks, suffixed `:<policy name>`: days no tier covers. "To check", dismissible. */
    CANCELLATION_TIER_GAP: 'cancellation_tier_gap',
    /** Prefix, suffixed `:<policy name>`: days two tiers both cover. "To check", dismissible. */
    CANCELLATION_TIER_OVERLAP: 'cancellation_tier_overlap',
} as const;
export type MissingFieldKeyValue = (typeof MISSING_FIELD_KEYS)[keyof typeof MISSING_FIELD_KEYS];

/**
 * What one row of the service price grid stands for: the first of vehicle,
 * pax band and person type that varies across the service's rates, else the
 * first one present, else a single row.
 */
export const SERVICE_GRID_ROW_DIMENSIONS = {
    VEHICLE: 'vehicle',
    PAX: 'pax',
    PERSON: 'person',
    SINGLE: 'single',
} as const;
export type ServiceGridRowDimensionValue = (typeof SERVICE_GRID_ROW_DIMENSIONS)[keyof typeof SERVICE_GRID_ROW_DIMENSIONS];

/**
 * What kind of service a `:Service` is (its `service_type`).
 *
 * Mirrors the `service_type` enum of the service extraction schema in
 * `App\Constants\AnthropicExtractionConstants` (there is no PHP enum class).
 */
export const SERVICE_TYPES = {
    TRANSFER: 'transfer',
    DAY_TOUR: 'day_tour',
    CHARTER_FLIGHT: 'charter_flight',
    MEET_GREET: 'meet_greet',
    CHAUFFEUR: 'chauffeur',
    VEHICLE_CHARTER: 'vehicle_charter',
    ACTIVITY: 'activity',
    ADDON: 'addon',
    OTHER: 'other',
} as const;
export type ServiceTypeValue = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

/** Tab key of the service price grid's table for rates with no `rate_set`. */
export const SERVICE_GRID_DEFAULT_SET_KEY = '__default__';

/** How urgently an attention item needs fixing. Blocking items stop publish/approve. */
export const ATTENTION_SEVERITIES = { BLOCKING: 'blocking', RECOMMENDED: 'recommended' } as const;
export type AttentionSeverityValue = (typeof ATTENTION_SEVERITIES)[keyof typeof ATTENTION_SEVERITIES];

/** What the needs-attention list gates: publishing in the graph editor, approving on the review page. */
export const ATTENTION_GATES = { PUBLISH: 'publish', APPROVE: 'approve' } as const;
export type AttentionGateValue = (typeof ATTENTION_GATES)[keyof typeof ATTENTION_GATES];

/** The one-click way out an attention item offers besides "Go". */
export const ATTENTION_ACTIONS = { DISMISS: 'dismiss', ROLL_FORWARD: 'roll_forward', CONFIRM: 'confirm' } as const;
export type AttentionActionValue = (typeof ATTENTION_ACTIONS)[keyof typeof ATTENTION_ACTIONS];

/** What a rate editor section's jump-bar button shows: something to fix, filled in, or untouched. */
export const RATE_SECTION_STATES = { DONE: 'done', ATTENTION: 'attention', EMPTY: 'empty' } as const;
export type RateSectionStateValue = (typeof RATE_SECTION_STATES)[keyof typeof RATE_SECTION_STATES];

/** `id` prefix of each rate editor `<section>`; the jump bar and `initialTab` scroll to `${prefix}${tab}`. */
export const RATE_SECTION_ID_PREFIX = 'rate-section-';

/**
 * Sections of the service editor sheet, in page order. A SERVICE selection's
 * `tab` names one, and the editor scrolls to it — the same contract as
 * {@see RATE_EDITOR_TABS}.
 */
export const SERVICE_EDITOR_SECTIONS = {
    DETAILS: 'details',
    ROUTE: 'route',
    PRICES: 'prices',
    INCLUSIONS: 'inclusions',
    IMAGES: 'images',
} as const;
export type ServiceEditorSectionValue = (typeof SERVICE_EDITOR_SECTIONS)[keyof typeof SERVICE_EDITOR_SECTIONS];

/** `id` prefix of each service editor `<section>`; the jump bar and `initialTab` scroll to `${prefix}${section}`. */
export const SERVICE_SECTION_ID_PREFIX = 'service-section-';

/** Segment key for the days of a month that no season or closed period covers. */
export const YEAR_STRIP_GAP_KEY = 'gap';

/** Segment and legend key for closed periods on the season year strip. */
export const YEAR_STRIP_CLOSED_KEY = 'closed';

/** Season colours on the year strip, cycled in season order. Semantic tokens only. */
export const YEAR_STRIP_TONES = ['bg-secondary/30', 'bg-primary/30', 'bg-primary/60', 'bg-success/40', 'bg-info/30'] as const;

/** Closed periods: muted, with a diagonal stripe so "shut" never reads as another season. */
export const YEAR_STRIP_CLOSED_TONE = 'bg-muted bg-[repeating-linear-gradient(135deg,hsl(var(--muted))_0_4px,hsl(var(--border))_4px_8px)]';

/**
 * Underline tab row of the property and provider pages. `overflow-x-auto`
 * forces `overflow-y` to `auto` too, so without `overflow-y-hidden` the row's
 * 2px active underline made a tiny vertical scrollbar appear.
 */
export const UNDERLINE_TAB_LIST_CLASS =
    'h-auto w-full justify-start gap-4 overflow-x-auto overflow-y-hidden rounded-none border-b bg-transparent p-0';

/** One trigger of {@see UNDERLINE_TAB_LIST_CLASS}. No `-mb-px`: the row clips vertically, so the underline sits just above the row's border. */
export const UNDERLINE_TAB_TRIGGER_CLASS =
    'hover:text-foreground data-[state=active]:border-primary gap-1.5 rounded-none border-b-2 border-transparent bg-transparent px-1 pt-1 pb-2 hover:bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none';

/**
 * A compact tab row for an editor nested inside another (a rate's sections
 * opened in its row of the rate review's Rooms & rates table): pill tabs on a
 * muted track, so they read as part of the row rather than as a second page
 * header under the property's own underlined tabs.
 */
export const PILL_TAB_LIST_CLASS = 'bg-muted/60 h-auto w-full justify-start gap-0.5 overflow-x-auto overflow-y-hidden rounded-md p-1';

/** One trigger of {@see PILL_TAB_LIST_CLASS}. */
export const PILL_TAB_TRIGGER_CLASS =
    'text-muted-foreground hover:text-foreground data-[state=active]:bg-card data-[state=active]:text-foreground gap-1.5 rounded-sm px-2.5 py-1 text-xs data-[state=active]:shadow-sm';

/** Provided by pages that own a Needs attention drawer; the attention card then emits `show-all` instead of expanding in place. */
export const ATTENTION_DRAWER_KEY = 'attention-drawer-available';

/**
 * The language a service is created in when none is given: the server's own
 * create-time default (`Neo4jService::serviceLanguage()`, the extraction
 * default). A service made in the editor starts with it, so an Undo that
 * writes it back never sends a blank (M-R).
 */
export const SERVICE_DEFAULT_LANGUAGE = 'English';
